/**
 * Motor de lógica de formulários (decisores, cálculos, visibilidade, validação).
 * ESPELHO de takaokabots-api/app/commons/forms/logicEngine.js.
 * Mantenha as duas versões sincronizadas.
 */

import { evaluateExpression, toNum } from "./expression";

// Únicos operadores que dispensam valor de comparação.
// neq com valor vazio lê como "campo preenchido", e há formulário publicado que depende
// disso. Tratar como regra incompleta esconderia o alvo de todo paciente — pior que o
// defeito que a guarda fecha, porque a pergunta some sem ninguém ver.
const DISPENSAM_VALOR = ["is_empty", "is_not_empty", "neq"];

export const OPERATORS = {
  eq: (a, b) => normalize(a) === normalize(b),
  neq: (a, b) => normalize(a) !== normalize(b),
  // Campo sem resposta não satisfaz comparação numérica: toNum("") é 0, e sem esta
  // guarda uma regra "peso < 50" dispararia para todo paciente que ainda não respondeu.
  gt: (a, b) => !isEmpty(a) && toNum(a) > toNum(b),
  gte: (a, b) => !isEmpty(a) && toNum(a) >= toNum(b),
  lt: (a, b) => !isEmpty(a) && toNum(a) < toNum(b),
  lte: (a, b) => !isEmpty(a) && toNum(a) <= toNum(b),
  // Em múltipla escolha compara opção a opção. O casamento por substring vale só para
  // texto livre: sobre um array ele casaria "nao_fumante" com "fuma".
  contains: (a, b) =>
    Array.isArray(a)
      ? asArray(a).map(normalize).includes(normalize(b))
      : asArray(a).map(normalize).includes(normalize(b)) ||
        String(a == null ? "" : a).toLowerCase().includes(String(b == null ? "" : b).toLowerCase()),
  not_contains: (a, b) => !OPERATORS.contains(a, b),
  starts_with: (a, b) => String(a == null ? "" : a).toLowerCase().startsWith(String(b == null ? "" : b).toLowerCase()),
  ends_with: (a, b) => String(a == null ? "" : a).toLowerCase().endsWith(String(b == null ? "" : b).toLowerCase()),
  in: (a, b) => asArray(b).map(normalize).includes(normalize(a)),
  not_in: (a, b) => !asArray(b).map(normalize).includes(normalize(a)),
  is_empty: (a) => isEmpty(a),
  is_not_empty: (a) => !isEmpty(a),
};

/** Rótulos PT-BR dos operadores para a UI do editor de lógica. */
export const OPERATOR_LABELS = {
  eq: "é igual a",
  neq: "é diferente de",
  gt: "é maior que",
  gte: "é maior ou igual a",
  lt: "é menor que",
  lte: "é menor ou igual a",
  contains: "contém",
  not_contains: "não contém",
  starts_with: "começa com",
  ends_with: "termina com",
  in: "está em",
  not_in: "não está em",
  is_empty: "está vazio",
  is_not_empty: "está preenchido",
};

function normalize(v) {
  if (v === null || v === undefined) return "";
  return String(v).trim().toLowerCase();
}

function asArray(v) {
  if (Array.isArray(v)) return v;
  if (v === null || v === undefined || v === "") return [];
  if (typeof v === "string" && v.includes(",")) return v.split(",").map((s) => s.trim());
  return [v];
}

export function isEmpty(v) {
  if (v === null || v === undefined) return true;
  if (Array.isArray(v)) return v.length === 0;
  // CPF de estrangeiro chega como {foreign, doc, nationality}: sem isto o String()
  // daria "[object Object]" e o campo obrigatório passaria com o documento em branco.
  if (typeof v === "object") return "doc" in v ? isEmpty(v.doc) : Object.keys(v).length === 0;
  return String(v).trim() === "";
}

export function buildContext(definition, valuesById) {
  const fields = (definition && definition.fields) || [];
  const byId = {};
  const nameToId = {};
  fields.forEach((f) => {
    byId[f.id] = f;
    if (f.name) nameToId[f.name] = f.id;
  });
  const values = valuesById || {};
  const scope = {};
  fields.forEach((f) => {
    const raw = values[f.id];
    const num = Array.isArray(raw) ? raw.length : raw;
    if (f.name) scope[f.name] = num;
    scope[f.id] = num;

    // pesos de risco por opção: <name>_score (soma) e <name>_max (máximo)
    if (Array.isArray(f.options)) {
      const selected = Array.isArray(raw) ? raw : (raw === undefined || raw === null || raw === "" ? [] : [raw]);
      const scores = selected.map((val) => {
        const opt = f.options.find((o) => String(o.value) === String(val));
        return opt && opt.score !== undefined && opt.score !== null && opt.score !== "" ? Number(opt.score) : 0;
      });
      const sum = scores.reduce((a, b) => a + (isNaN(b) ? 0 : b), 0);
      const max = scores.length ? Math.max(...scores.map((s) => (isNaN(s) ? 0 : s))) : 0;
      if (f.name) { scope[`${f.name}_score`] = sum; scope[`${f.name}_max`] = max; }
      scope[`${f.id}_score`] = sum;
      scope[`${f.id}_max`] = max;
    }
  });
  return { fields, byId, nameToId, values, scope };
}

function resolveRef(ref, ctx) {
  if (ctx.byId[ref]) return ctx.values[ref];
  if (ctx.nameToId[ref]) return ctx.values[ctx.nameToId[ref]];
  return ctx.scope[ref];
}

export function evaluateCondition(cond, ctx) {
  if (!cond) return true;
  // O editor antigo, ao trocar E/OU numa regra solta, gravava `op` ao lado de field/operator
  // sem `rules`. Lido como grupo, isso era um grupo vazio (falso) e escondia a pergunta de
  // todo paciente; a intenção de quem montou é a própria regra.
  if ((cond.op === "and" || cond.op === "or") && (Array.isArray(cond.rules) || !cond.field)) {
    const rules = cond.rules || [];
    // Grupo sem regra nenhuma não expressa critério: mostrava o campo para todo paciente.
    if (rules.length === 0) return false;
    if (cond.op === "and") return rules.every((r) => evaluateCondition(r, ctx));
    return rules.some((r) => evaluateCondition(r, ctx));
  }
  // Regra incompleta não expressa critério nenhum e não pode governar o que o paciente
  // vê. Solta, o resultado dependia do operador: eq/contains casavam com quem não
  // respondeu, in/gt casavam com ninguém — nos dois casos sem intenção de quem montou.
  // Estes testes vêm antes de OPERATORS porque a forma mais crua de regra pela metade é
  // justamente a que não tem operador: sem eles, regra vazia e operador com erro de
  // digitação escapavam pelo caminho do operador desconhecido.
  if (!cond.field) return false;
  const fn = OPERATORS[cond.operator];
  if (!fn) return false;
  if (!DISPENSAM_VALOR.includes(cond.operator) && (cond.value === undefined || cond.value === null || String(cond.value).trim() === "")) return false;
  return !!fn(resolveRef(cond.field, ctx), cond.value);
}

function computeCalculations(definition, ctx) {
  const calcs = (definition && definition.calculations) || [];
  const computed = {};
  const targets = {};
  // Numa passada só, um cálculo declarado antes da sua dependência leria 0 em silêncio.
  // Repetimos até os valores pararem de mudar, no máximo uma passada por cálculo — teto
  // suficiente para qualquer cadeia sem ciclo. Havendo ciclo, para no teto e mantém o
  // último valor em vez de girar.
  for (let pass = 0; pass < calcs.length; pass++) {
    let changed = false;
    calcs.forEach((c) => {
      const value = evaluateExpression(c.expression, ctx.scope);
      if (computed[c.id] !== value) {
        computed[c.id] = value;
        changed = true;
      }
      if (c.name) ctx.scope[c.name] = value;
      if (c.target) {
        ctx.scope[c.target] = value;
        ctx.values[c.target] = value;
        targets[c.target] = value;
      }
    });
    if (!changed) break;
  }
  return { computed, targets };
}

function resolveFieldStates(definition, ctx) {
  const fields = ctx.fields;
  const visible = {};
  const required = {};
  fields.forEach((f) => {
    let vis = f.hidden ? false : true;
    if (vis && f.visibleIf) vis = evaluateCondition(f.visibleIf, ctx);
    visible[f.id] = vis;
    required[f.id] = !!f.required;
  });

  const logic = (definition && definition.logic) || [];
  logic.forEach((rule) => {
    if (rule.enabled === false) return;
    if (!evaluateCondition(rule.when, ctx)) return;
    (rule.actions || []).forEach((action) => {
      const target = action.target;
      if (target === undefined || target === null) return;
      switch (action.type) {
        case "show": visible[target] = true; break;
        case "hide": visible[target] = false; break;
        case "require": required[target] = true; break;
        case "optional": required[target] = false; break;
        default: break;
      }
    });
  });
  return { visible, required };
}

function resolveReachablePages(definition, ctx) {
  const pages = (definition && definition.pages) || [];
  if (pages.length === 0) return { reachable: {}, skippedFieldIds: new Set() };
  const reachable = {};
  pages.forEach((p) => { reachable[p.id] = true; });

  const logic = (definition && definition.logic) || [];
  logic.forEach((rule) => {
    if (rule.enabled === false) return;
    if (!evaluateCondition(rule.when, ctx)) return;
    (rule.actions || []).forEach((action) => {
      if (action.type === "skipTo" && action.target) {
        const fromIdx = pages.findIndex((p) => p.id === action.fromPage);
        const toIdx = pages.findIndex((p) => p.id === action.target);
        if (fromIdx >= 0 && toIdx > fromIdx) {
          for (let i = fromIdx + 1; i < toIdx; i++) reachable[pages[i].id] = false;
        }
      }
    });
  });

  const skippedFieldIds = new Set();
  pages.forEach((p) => {
    if (!reachable[p.id]) (p.fieldIds || []).forEach((id) => skippedFieldIds.add(id));
  });
  return { reachable, skippedFieldIds };
}

const limitesAvisados = new Set();

/**
 * Limite de data. `min`/`max` comparam com toNum, e data vira NaN: quem configurasse
 * 1900-01-01 no campo de nascimento não barrava nada. A palavra "hoje" vale como limite
 * porque a data de hoje muda todo dia e não dá para deixar fixa na definição.
 */
function limiteDeData(limite) {
  if (limite === undefined || limite === null || limite === "") return null;
  const texto = String(limite).trim().toLowerCase();
  if (texto === "hoje" || texto === "today") {
    const hoje = new Date();
    const mes = String(hoje.getMonth() + 1).padStart(2, "0");
    const dia = String(hoje.getDate()).padStart(2, "0");
    return `${hoje.getFullYear()}-${mes}-${dia}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(texto)) return texto;
  // Limite fora do formato não valida nada: sem o aviso, quem configurasse 31/12/2025
  // no builder não receberia erro nem na tela nem no servidor. Uma vez por limite:
  // isto roda a cada campo de cada resposta.
  if (!limitesAvisados.has(texto)) {
    limitesAvisados.add(texto);
    console.warn('Limite de data ignorado, use aaaa-mm-dd ou "hoje":', limite);
  }
  return null;
}

function dataExiste(data) {
  const [ano, mes, dia] = data.split("-").map(Number);
  const d = new Date(Date.UTC(ano, mes - 1, dia));
  return d.getUTCFullYear() === ano && d.getUTCMonth() === mes - 1 && d.getUTCDate() === dia;
}

function erroDeData(valor, v) {
  const data = String(valor || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(data)) return "Data inválida";
  // O endpoint público aceita JSON cru, então não dá para contar com o <input type="date">:
  // 2025-02-30 casa com o formato e viraria 02/03/2025 na conta de idade.
  if (!dataExiste(data)) return "Data inválida";
  const minimo = limiteDeData(v.min);
  const maximo = limiteDeData(v.max);
  // Comparação de texto basta: o formato ISO já ordena por ano, mês e dia.
  if (minimo && data < minimo) return `Data mínima é ${formatarData(minimo)}`;
  if (maximo && data > maximo) return maximo === limiteDeData("hoje") ? "A data não pode ser futura" : `Data máxima é ${formatarData(maximo)}`;
  return null;
}

function formatarData(iso) {
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
}

function validateField(field, value) {
  // CPF estrangeiro: valor é objeto { foreign, doc, nationality } — exige documento e nacionalidade
  if (field.allowForeignCpf && value && typeof value === "object" && value.foreign) {
    if (!value.doc || String(value.doc).trim() === "") return "Informe o RNE / Passaporte";
    if (!value.nationality || String(value.nationality).trim() === "") return "Selecione a nacionalidade";
    return null;
  }
  const v = field.validation || {};
  const str = value == null ? "" : String(value);
  if (v.minLength && str.length < v.minLength) return `Mínimo de ${v.minLength} caracteres`;
  if (v.maxLength && str.length > v.maxLength) return `Máximo de ${v.maxLength} caracteres`;
  if (v.min !== undefined && v.min !== null && v.min !== "" && toNum(value) < toNum(v.min)) return `Valor mínimo é ${v.min}`;
  if (v.max !== undefined && v.max !== null && v.max !== "" && toNum(value) > toNum(v.max)) return `Valor máximo é ${v.max}`;
  if (v.pattern) {
    try { if (!new RegExp(v.pattern).test(str)) return "Formato inválido"; } catch (e) { /* ignora */ }
  }
  if (field.type === "date" && str) {
    const erroData = erroDeData(value, v);
    if (erroData) return erroData;
  }
  if (field.type === "email" && str && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str)) return "E-mail inválido";
  return null;
}

/**
 * Valores que o paciente de fato respondeu: sem os campos escondidos por lógica e sem os
 * das páginas puladas. Sem isso, uma resposta que o paciente retirou ao corrigir o
 * formulário continua alimentando cálculo e escore.
 *
 * Repete a filtragem até o resultado parar de mudar. Esconder um campo pode esconder o
 * seguinte, e uma passada só fechava cadeias de dois elos: a partir do terceiro, o que a
 * tela mostrou e o que o banco gravava divergiam. Como cada passada só remove campos, o
 * número de campos é o teto — uma regra circular para nele em vez de iterar sem fim.
 */
export function effectiveValues(definition, valuesById) {
  const campos = (definition && definition.fields) || [];
  let atual = Object.assign({}, valuesById);
  for (let i = 0; i <= campos.length; i++) {
    const proximo = filtrarCamposOcultos(definition, atual);
    if (Object.keys(proximo).length === Object.keys(atual).length) return proximo;
    atual = proximo;
  }
  return atual;
}

function filtrarCamposOcultos(definition, valuesById) {
  const ctx = buildContext(definition, Object.assign({}, valuesById));
  computeCalculations(definition, ctx);
  const { visible } = resolveFieldStates(definition, ctx);
  const { skippedFieldIds } = resolveReachablePages(definition, ctx);

  const out = Object.assign({}, valuesById);
  ((definition && definition.fields) || []).forEach((f) => {
    if (visible[f.id] === false || skippedFieldIds.has(f.id)) delete out[f.id];
  });
  return out;
}


export function evaluateForm(definition, valuesById) {
  const ctx = buildContext(definition, effectiveValues(definition, valuesById));
  const { computed, targets } = computeCalculations(definition, ctx);
  const { visible, required } = resolveFieldStates(definition, ctx);
  const { skippedFieldIds } = resolveReachablePages(definition, ctx);

  const errors = {};
  ctx.fields.forEach((f) => {
    if (skippedFieldIds.has(f.id)) return;
    if (!visible[f.id]) return;
    if (f.type === "section" || f.type === "heading" || f.type === "paragraph" || f.type === "cover") return;
    // O campo está visível, então vale o que o paciente digitou, não o contexto filtrado.
    // Um campo que reaparece porque o anterior sumiu era validado contra undefined: a tela
    // pedia a resposta, o paciente digitava, e o envio seguia acusando "campo obrigatório"
    // — formulário intransponível, sem mensagem que explicasse. O contexto só entra quando
    // não há resposta crua, que é o caso do alvo calculado pelo servidor.
    const crus = valuesById || {};
    const value = crus[f.id] !== undefined ? crus[f.id] : ctx.values[f.id];
    if (required[f.id] && isEmpty(value)) {
      errors[f.id] = "Campo obrigatório";
      return;
    }
    if (!isEmpty(value)) {
      const err = validateField(f, value);
      if (err) errors[f.id] = err;
    }
  });

  return {
    computed,
    targets,
    visible,
    required,
    skippedFieldIds: Array.from(skippedFieldIds),
    errors,
    valid: Object.keys(errors).length === 0,
  };
}
